import React from 'react';
import { Document, Page, Text, View } from '@react-pdf/renderer';
import { billingPdfRows, deliveryPdfRows } from './shared/billingDeliveryPdfRows';
import { CompanyPdfHeader } from './shared/CompanyPdfHeader';
import { LabeledPdfRows } from './shared/LabeledPdfBlock';
import { NoteLinesPdfTable } from './shared/NoteLinesPdfTable';
import { PdfLabeledSections } from './shared/PdfLabeledSections';
import { PdfPrintScaleProvider, usePdfStyles } from './shared/PdfPrintScaleContext';

function DeliveryNotePdfPage({ data }) {
    const pdfStyles = usePdfStyles();
    const subtitleParts = [`Order #${data.orderNumber || '—'}`];
    if (data.invoiceNumber) {
        subtitleParts.push(`Invoice #${data.invoiceNumber}`);
    }
    const subtitle = subtitleParts.join(' · ');

    const summaryRows = [
        { label: 'Order status', value: data.status },
        { label: 'Order date', value: data.orderDate },
    ];

    return (
        <Page size="A4" style={pdfStyles.page}>
            <CompanyPdfHeader title="Delivery note" subtitle={subtitle} />

            <Text style={pdfStyles.sectionTitle}>Summary</Text>
            <View style={pdfStyles.twoCol}>
                <View style={pdfStyles.col}>
                    <LabeledPdfRows rows={summaryRows} />
                </View>
                <View style={pdfStyles.col} />
            </View>

            {data.note ? (
                <View style={{ marginTop: 8 }}>
                    <Text style={pdfStyles.sectionTitle}>Order note</Text>
                    <Text style={{ fontSize: 8 }}>{data.note}</Text>
                </View>
            ) : null}

            <Text style={pdfStyles.sectionTitle}>Items to deliver</Text>
            <NoteLinesPdfTable lines={data.lines || []} />

            <PdfLabeledSections
                sections={[
                    { title: 'Billing', rows: billingPdfRows(data.billing), wrap: false },
                    { title: 'Delivery', rows: deliveryPdfRows(data.delivery), wrap: false },
                ]}
            />
        </Page>
    );
}

/**
 * Delivery note PDF — feed {@link normalizeDeliveryNoteForPdf} output as `data`.
 * @param {{ data: Record<string, unknown>; scale?: number }} props
 */
export function DeliveryNotePdfDocument({ data, scale = 1 }) {
    return (
        <Document title={`Delivery note ${data.orderNumber || ''}`}>
            <PdfPrintScaleProvider scale={scale}>
                <DeliveryNotePdfPage data={data} />
            </PdfPrintScaleProvider>
        </Document>
    );
}
